/**
 * BrandMark — canonical PolkDesQ brand lockup.
 *
 * Renders a crafted "Q" glyph (¾-circle bowl + diagonal tail + accent notch)
 * inside an indigo tile, paired with the wordmark "PolkDesQ" where the final
 * letter inherits the tile colour so glyph and typography rhyme.
 *
 * Pure SVG + CSS tokens — no new dependencies, theme-aware in both light and
 * dark via `var(--pd-focus)` / `var(--pd-focus-color-hover)`.
 *
 * Variants:
 *   - 'lockup'   → mark + wordmark in a horizontal row (default)
 *   - 'mark'     → just the icon tile
 *   - 'wordmark' → just the typography (no tile)
 *
 * Sizes: sm | md | lg
 */

const SIZE_TABLE = {
  sm: { tile: 32, glyph: 18, word: 13, gap: 10, radius: 10 },
  md: { tile: 44, glyph: 24, word: 16, gap: 12, radius: 14 },
  lg: { tile: 64, glyph: 34, word: 24, gap: 16, radius: 20 },
};

function QGlyph({ size, color = 'currentColor' }) {
  // 32×32 viewBox — tuned so the mark reads as "Q" even at 18px.
  // Bowl: a ¾-circle opening toward the bottom-right.
  // Tail: a short diagonal exiting through the open quadrant.
  // Notch: small accent dot at ~2 o'clock for craft.
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
      data-testid="brand-mark-q-glyph"
    >
      <path
        d="M16 4 a12 12 0 1 1 -8.485 20.485"
        stroke={color}
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <path
        d="M19 19 L27 27"
        stroke={color}
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <circle cx="22.5" cy="9.5" r="1.6" fill={color} />
    </svg>
  );
}

function Tile({ tile, glyph, radius, testId, className = '' }) {
  return (
    <div
      data-testid={`${testId}-tile`}
      className={`pd-brand-tile ${className}`.trim()}
      style={{
        width: tile,
        height: tile,
        borderRadius: radius,
      }}
    >
      <QGlyph size={glyph} color="#FFFFFF" />
    </div>
  );
}

function Wordmark({ word, testId }) {
  return (
    <span
      data-testid={`${testId}-wordmark`}
      className="pd-brand-wordmark"
      style={{ fontSize: word }}
    >
      PolkDes<span className="pd-brand-wordmark-q">Q</span>
    </span>
  );
}

export function BrandMark({
  variant = 'lockup',
  size = 'md',
  testId = 'brand-mark',
  className = '',
}) {
  const s = SIZE_TABLE[size] || SIZE_TABLE.md;

  if (variant === 'mark') {
    return <Tile tile={s.tile} glyph={s.glyph} radius={s.radius} testId={testId} className={className} />;
  }
  if (variant === 'wordmark') {
    return (
      <span
        data-testid={testId}
        className={`pd-brand-wordmark-outer ${className}`.trim()}
        style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}
      >
        <Wordmark word={s.word} testId={testId} />
      </span>
    );
  }
  return (
    <div
      data-testid={testId}
      className={`pd-brand-lockup ${className}`}
      style={{ gap: s.gap }}
    >
      <Tile tile={s.tile} glyph={s.glyph} radius={s.radius} testId={testId} />
      <Wordmark word={s.word} testId={testId} />
    </div>
  );
}

export default BrandMark;
